<script setup lang="ts">
import { computed } from 'vue'
import Card from '@/components/ui/Card.vue'
import Skeleton from '@/components/ui/Skeleton.vue'
import type { Component } from 'vue'

export type StatsAccent = 'blue' | 'green' | 'orange' | 'purple'

interface Props {
  label: string
  /** already-formatted primary value, e.g. "12.3 MB/s" */
  value: string
  icon: Component
  accent?: StatsAccent
  /** optional secondary line under the value */
  hint?: string
  loading?: boolean
  /** sparkline samples (raw numbers); rendered when 2+ points are given */
  trend?: number[]
  /** v3.13.58: 可点击 (跳转关联页面) — 显示指针并触发 activate 事件 */
  clickable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  accent: 'blue',
  loading: false,
  trend: () => [],
  clickable: false,
})

const emit = defineEmits<{ activate: [] }>()

/**
 * Accent palette. `purple` has no design token, so it falls back to a literal
 * violet that is legible on both themes; the others map onto tokens.
 */
const accentClass = computed(() => {
  switch (props.accent) {
    case 'green':
      return { icon: 'bg-success/15 text-success', stroke: 'hsl(var(--success))' }
    case 'orange':
      return { icon: 'bg-warning/15 text-warning', stroke: 'hsl(var(--warning))' }
    case 'purple':
      return { icon: 'bg-[hsl(265_85%_60%/0.15)] text-[hsl(265_85%_60%)] dark:text-[hsl(265_90%_75%)]', stroke: 'hsl(265 85% 60%)' }
    default:
      return { icon: 'bg-brand-500/15 text-brand-600 dark:text-brand-300', stroke: 'hsl(var(--brand-500))' }
  }
})

/** Normalise the trend into a 0..1 polyline inside a 100x28 viewBox. */
const sparkline = computed(() => {
  const data = props.trend
  if (!data || data.length < 2) return ''
  const max = Math.max(...data)
  const min = Math.min(...data)
  const span = max - min || 1
  const w = 100
  const h = 28
  const step = w / (data.length - 1)
  return data
    .map((v, i) => {
      const x = i * step
      const y = h - ((v - min) / span) * (h - 2) - 1
      return `${x.toFixed(2)},${y.toFixed(2)}`
    })
    .join(' ')
})
</script>

<template>
  <Card hoverable :class="clickable ? 'cursor-pointer select-none' : ''" @click="clickable && emit('activate')">
    <div class="flex items-start justify-between gap-3">
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-2">
          <span class="rounded-md p-1.5" :class="accentClass.icon">
            <component :is="icon" :size="16" />
          </span>
          <span class="truncate text-xs font-medium text-fg-muted">{{ label }}</span>
        </div>

        <div class="mt-3">
          <Skeleton v-if="loading" width="6rem" height="1.75rem" />
          <p v-else class="truncate text-2xl font-semibold tabular-nums text-fg" :title="value">
            {{ value }}
          </p>
        </div>

        <p v-if="hint && !loading" class="mt-1 truncate text-xs text-fg-subtle">{{ hint }}</p>
      </div>

      <svg
        v-if="sparkline && !loading"
        class="h-8 w-16 shrink-0 self-end"
        viewBox="0 0 100 28"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <polyline
          :points="sparkline"
          fill="none"
          :stroke="accentClass.stroke"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          vector-effect="non-scaling-stroke"
          opacity="0.85"
        />
      </svg>
    </div>
  </Card>
</template>
